import React, { useEffect, useRef, useState, useMemo } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { AutoSizer, List } from 'react-virtualized';
import { Popover, LinearProgress } from '@material-ui/core';
import { AuthChecker, Button, TableHead, SelectionMenu, TableRow } from '../../atomicComponents';
import styles from './CsvPreviewTable.module.css';
import { columnsPropType } from "../../types";

function filterBySearchTexts(fieldToSearchTextMap, data, columns) {
  const fieldToSearchFnMap = columns.reduce((acc, col) => {
    if (col.searchFn) return { ...acc, [col.field]: col.searchFn };
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToSearchTextMap).forEach(entry => {
    const [field, searchText] = entry;
    const defaultSearchFn = (fieldValue, searchValue) =>
      fieldValue.toLowerCase().startsWith(searchValue);

    const searchFn = fieldToSearchFnMap[field] || defaultSearchFn;

    filteredData = filteredData.filter(
      row => !searchText || !row[field] || searchFn(row[field], searchText),
    );
  });
  return filteredData;
}

function filterBySelectedFilters(fieldToFiltersSetMap, data, columns) {
  const fieldToFilterFnMap = columns.reduce((acc, col) => {
    if (col.filterFn) return { ...acc, [col.field]: col.filterFn };
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToFiltersSetMap).forEach((entry, idx) => {
    const [field, filtersSet] = entry;
    const filterFn = fieldToFilterFnMap[field];

    filteredData = filteredData.filter(row => {
      if (!filterFn) return !filtersSet?.size || filtersSet.has(row[field]);
      if (!idx) {
        console.log(' [...filtersSet]', [...filtersSet]);
      }

      return (
        !filtersSet?.size ||
        [...filtersSet].some(filter => filterFn(row, filter.value))
      );
    });
  });
  return filteredData;
}

function deepCompareEqualsColumnsArray(oldArray, newArray) {
  if (oldArray?.length !== newArray?.length) return false;
  const zippedArray = oldArray.map((e, i) => [e, newArray[i]]);
  return zippedArray.every(tuple => tuple[0].title === tuple[1].title);
}

function useDeepCompareColumnArraysMemoize(value) {
  const ref = useRef();
  // it can be done by using useMemo as well
  // but useRef is rather cleaner and easier

  if (!deepCompareEqualsColumnsArray(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepCompareColumnArraysEffect(callback, dependencies) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, dependencies.map(useDeepCompareColumnArraysMemoize));
}

const Table = ({
  rows: data,
  columns: originalColumns,
  classNames = {},
  rowHeight = 38,
  headerHeight,
  defaultColumns: originalDefaultColumns,
  primaryAction,
  secondaryActions = [],
  setHasActiveEdits,
  setQuantity,
  isDataLoading,
  tableAction,
  isActionsBarAbsent,
  requiredButtonList,
  getSelectedRows,
}) => {
  const CSV_LIST_ID = 'csv-list';

  const [rowsData, setRowsData] = useState({
    data: [...data],
    filteredData: [...data],
  });

  const [fieldToSearchTextMap, setFieldToSearchTextMap] = useState({});
  const [isEllipsisPopupOpen, setIsEllipsisPopupOpen] = useState(false);
  const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
  const [ellipsisPopupValue, setEllipsisPopupValue] = useState(null);
  const [fieldToFiltersSetMap, setFieldToFiltersSetMap] = useState({});

  const closeEllipsisPopup = () => setIsEllipsisPopupOpen(false);

  const vListRef = useRef(null);
  const csvPreviewTableHeadRef = useRef(null);

  const handleListScroll = e => {
    csvPreviewTableHeadRef.current.scrollLeft = e.target.scrollLeft;
  };

  const syncListToTableHead = e => {
    vListRef.current.scrollLeft = e.target.scrollLeft;
  };

  useEffect(() => {
    // eslint-disable-next-line prefer-destructuring
    vListRef.current = document.getElementById(CSV_LIST_ID);

    if (vListRef.current)
      vListRef.current.addEventListener('scroll', handleListScroll);
    csvPreviewTableHeadRef.current.addEventListener('scroll', syncListToTableHead);
  }, []);

  useEffect(() => {
    setQuantity(rowsData.filteredData.length);
  }, [setQuantity, rowsData]);

  const handlePopoverOpen = (event, val) => {
    if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
      setEllipsisPopupValue(val);
      setIsEllipsisPopupOpen(true);
      setEllipsisAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setIsEllipsisPopupOpen(false);
    setEllipsisAnchorEl(null);
  };

  const toggleSelectAllRows = () =>
    setRowsData(prev => {
      const visibleRowIdsSet = new Set(prev.filteredData.map(row => row.id));
      const isSelected = !prev.filteredData.every(row => row.isSelected);
      return {
        ...prev,
        filteredData: prev.filteredData.map(row => ({ ...row, isSelected })),
        data: prev.data.map(row => {
          if (visibleRowIdsSet.has(row.id)) return { ...row, isSelected };
          return row;
        }),
      };
    });

  const handleRowSelection = selectedId => {
    setRowsData(prev => ({
      ...prev,
      data: prev.data.map(row =>
        row.id === selectedId ? { ...row, isSelected: !row.isSelected } : row,
      ),
    }));
  };

  useEffect(() => {
    if (setHasActiveEdits) {
      const areEditsActive = rowsData.data.some(row => row.isSelected);
      setHasActiveEdits(areEditsActive);
    }
  }, [rowsData.data, setHasActiveEdits]);

  const defaultColumns = useMemo(
    () =>
      [
        {
          title: '#',
          field: '_serialNumber',
          width: 40,
          render: (_, idx) => idx,
          headerStyles: {
            paddingLeft: 7,
            paddingRight: 2,
          },
        },
        primaryAction || secondaryActions?.length
          ? {
            title: 'select',
            field: '_select',
            width: 30,
            renderHeader: rows => (
              <input
                type="checkbox"
                onChange={toggleSelectAllRows}
                // eslint-disable-next-line react/destructuring-assignment
                checked={rows?.length && rows.every(row => row.isSelected)}
              />
            ),
            // eslint-disable-next-line react/prop-types
            render: ({ isSelected, id }) => (
              <input
                style={{ marginTop: 0, marginBottom: 0 }}
                type="checkbox"
                checked={isSelected}
                onChange={() => {
                  handleRowSelection(id);
                }}
                data-testid={`select-row-${id}`}
                id={`select-row-${id}`}
                name={`select-row-${id}`}
              />
            ),
          }
          : null,
        ...originalDefaultColumns,
      ].filter(i => i),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [originalDefaultColumns],
  );
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);

  const noOfFixedColumns = primaryAction || secondaryActions?.length ? 2 : 1;

  const [isCustomizeTableMenuOpen, setIsCustomizeTableMenuOpen] = useState(
    false,
  );

  const [showCopyAlert, setShowCopyAlert] = useState(false);

  const customizeTableButtonRef = useRef(null);

  const reorder = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const reorderHeaders = result => {
    // dropped outside the list or dropped at serial number place
    if (!result.destination || result.destination.index < noOfFixedColumns) {
      return;
    }

    const reorderedColumns = reorder(
      selectedColumns,
      result.source.index,
      result.destination.index,
    );
    setSelectedColumns(reorderedColumns);
  };

  const rowRenderer = ({ style, index, key }) => {
    const handleCopy = text => {
      navigator.clipboard.writeText(text);
      setShowCopyAlert(true);
    };

    return (
      <TableRow
        index={index}
        style={style}
        key={key}
        row={rowsData.filteredData[index]}
        columns={selectedColumns}
        classNames={{ ...classNames }}
        rowHeight={rowHeight}
        onCopy={handleCopy}
        handlePopoverOpen={handlePopoverOpen}
        handlePopoverClose={handlePopoverClose}
      />
    );
  };

  const handleColumnSelectionChange = columnTitle => {
    if (selectedColumns.map(({ title }) => title).includes(columnTitle))
      return setSelectedColumns(prev =>
        prev.filter(column => column.title !== columnTitle),
      );
    return setSelectedColumns(prev => [
      ...prev,
      ...originalColumns.filter(({ title }) => title === columnTitle),
    ]);
  };

  const applyColumnsSelections = columnSelections => {
    setSelectedColumns([
      ...defaultColumns.slice(
        0,
        defaultColumns.length - originalDefaultColumns.length,
      ),
      ...originalColumns.filter(col => columnSelections.includes(col.title)),
    ]);
  };

  useEffect(() => {
    if (showCopyAlert) setShowCopyAlert(false);
  }, [showCopyAlert]);

  // taken from https://stackoverflow.com/questions/54095994/react-useeffect-comparing-objects
  useDeepCompareColumnArraysEffect(() => {
    setSelectedColumns(defaultColumns);
  }, [defaultColumns]);

  useEffect(() => {
    setRowsData(prevState => {
      const dataFilteredBySearchTexts = filterBySearchTexts(
        fieldToSearchTextMap,
        prevState.data,
        selectedColumns,
      );

      const dataFilteredBySearchAndFilters = filterBySelectedFilters(
        fieldToFiltersSetMap,
        dataFilteredBySearchTexts,
        selectedColumns,
      );

      return { ...prevState, filteredData: dataFilteredBySearchAndFilters };
    });
  }, [
    selectedColumns,
    fieldToFiltersSetMap,
    fieldToSearchTextMap,
    rowsData.data,
  ]);

  useEffect(() => {
    setRowsData({
      data: [...data],
      filteredData: [...data],
    });
  }, [data]);

  useEffect(() => {
    if (getSelectedRows) {
      getSelectedRows(rowsData.data.filter(row => row.isSelected));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsData.data]);

  return (
    <>
      <div
        style={{ width: '100%', height: '100%' }}
        className={clsx(
          styles.baseContainer,
          classNames.tableContainerClassName,
        )}
      >
        <div
          style={{ width: '100%' }}
          className={clsx(
            styles.basicTableContainer,
            classNames.tableContainerClassName,
          )}
        >
          <AutoSizer>
            {({ width: windowWidth, height: windowHeight }) => {
              const width = windowWidth;
              const height = windowHeight;
              const listHeight = height - rowHeight - 2;
              return (
                <div
                  className={clsx(styles.basicTable, classNames.tableClassName)}
                  style={{
                    height,
                    width,
                  }}
                >
                  <TableHead
                    columns={selectedColumns}
                    ref={csvPreviewTableHeadRef}
                    className={classNames.tableHeadClassName}
                    height={headerHeight || rowHeight}
                    reorderHeaders={reorderHeaders}
                    rows={rowsData.filteredData}
                    allRows={rowsData.data}
                    fieldToSearchTextMap={fieldToSearchTextMap}
                    setSearchTextForTitleCurry={field => value =>
                      setFieldToSearchTextMap(prev => ({
                        ...prev,
                        [field]: value.toLowerCase(),
                      }))}
                    noOfFixedColumns={noOfFixedColumns}
                    fieldToFiltersSetMap={fieldToFiltersSetMap}
                    setFiltersForFieldCurry={field => selectedFilters =>
                      setFieldToFiltersSetMap(prev => ({
                        ...prev,
                        [field]: new Set(selectedFilters),
                      }))}
                  />
                  <List
                    id={CSV_LIST_ID}
                    ref={vListRef}
                    containerStyle={{ overflow: 'initial' }}
                    rowRenderer={rowRenderer}
                    height={listHeight}
                    rowHeight={rowHeight}
                    rowCount={rowsData.filteredData.length}
                    width={width}
                    className={clsx(
                      styles.scrollBar,
                      styles.heightAdjustedListScroll,
                    )}
                    overscanRowCount={10}
                  />
                  {isDataLoading && (
                    <div>
                      <LinearProgress />
                    </div>
                  )}
                </div>
              );
            }}
          </AutoSizer>
        </div>
        {!isActionsBarAbsent && (
          <div className={styles.actionsBar}>
            <Button
              className={styles.actionButton}
              variant="tertiary"
              onClick={() => setIsCustomizeTableMenuOpen(true)}
              ref={customizeTableButtonRef}
            >
              CUSTOMIZE TABLE
            </Button>
            {secondaryActions.map(action => (
              <AuthChecker operation={action.authOperation}>
                {isAuthorized => (
                  <Button
                    variant="primary"
                    className={styles.actionButton}
                    style={{
                      backgroundColor: action.backgroundColor,
                    }}
                    onClick={() =>
                      action.actionFn(
                        rowsData.data.filter(row => row.isSelected),
                      )
                    }
                    disabled={
                      action.authOperation &&
                      (!isAuthorized ||
                        !action.shouldEnable(
                          rowsData.data.filter(row => row.isSelected),
                        ))
                    }
                  >
                    {action.name}
                  </Button>
                )}
              </AuthChecker>
            ))}
            {Object.values(fieldToFiltersSetMap).every(
              filtersSet => !filtersSet?.size,
            ) || (
                <Button
                  className={clsx(styles.actionButton, styles.clearFiltersButton)}
                  variant="secondary"
                  onClick={() => setFieldToFiltersSetMap({})}
                >
                  Clear Filters
                </Button>
              )}
            {requiredButtonList?.map(button => (
              <Button className={styles.requiredBt} onClick={button?.action}>
                {button.title}
              </Button>
            ))}
            {/* eslint-disable-next-line no-nested-ternary */}
            {tableAction ? (
              <Button
                className={clsx(
                  styles.actionButton,
                  styles.primaryActionButton,
                )}
                onClick={() => tableAction?.actionFn()}
                data-testid={tableAction?.name.replace(' ', '-')}
              >
                {tableAction?.name}
              </Button>
            ) : primaryAction ? (
              (primaryAction.component && (
                <primaryAction.component
                  {...primaryAction.args}
                  disabled={
                    // eslint-disable-next-line react/prop-types
                    !primaryAction.shouldEnable(
                      rowsData.data.filter(row => row.isSelected),
                    )
                  }
                  id={primaryAction?.name.replace(' ', '-')}
                />
              )) || (
                <Button
                  className={clsx(
                    styles.actionButton,
                    styles.primaryActionButton,
                  )}
                  onClick={() => primaryAction?.actionFn()}
                  data-testid={primaryAction?.name.replace(' ', '-')}
                  disabled={
                    // eslint-disable-next-line react/prop-types
                    !primaryAction.shouldEnable(
                      rowsData.data.filter(row => row.isSelected),
                    )
                  }
                >
                  {primaryAction?.name}
                </Button>
              )
            ) : (
              <></>
            )}
          </div>
        )}
      </div>
      {(isActionsBarAbsent) ? (
        Object.values(fieldToFiltersSetMap).every(
          filtersSet => !filtersSet?.size,
        ) || (
          <Button
            className={clsx(
              styles.actionButton,
              styles.clearFiltersButtonInTag,
            )}
            variant="secondary"
            onClick={() => setFieldToFiltersSetMap({})}
          >
            Clear Filters
          </Button>
        )
      ) : (
        Object.values(fieldToFiltersSetMap).every(
          filtersSet => !filtersSet?.size,
        ) || (
          <Button
            className={clsx(
              styles.actionButton,
              styles.clearFiltersInCsvPreview,
            )}
            variant="secondary"
            onClick={() => setFieldToFiltersSetMap({})}
          >
            Clear Filters
          </Button>
        )
      )}

      <SelectionMenu
        anchorEl={customizeTableButtonRef.current}
        open={isCustomizeTableMenuOpen}
        handleClose={() => setIsCustomizeTableMenuOpen(false)}
        options={originalColumns.map(({ title }) => title)}
        currentSelections={selectedColumns
          .filter(({ title }) => title !== 'select' && title !== '#')
          .map(({ title }) => title)}
        handleChange={handleColumnSelectionChange}
        resetToDefault={() => setSelectedColumns([...defaultColumns])}
        applySelections={applyColumnsSelections}
      />
      <Popover
        open={isEllipsisPopupOpen}
        anchorEl={ellipsisAnchorEl}
        onClose={closeEllipsisPopup}
        className={styles.popover}
        classes={{
          paper: styles.popoverPaper,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {ellipsisPopupValue}
      </Popover>
      <div
        className={clsx(
          styles.copyAlert,
          showCopyAlert ? styles.visible : styles.hidden,
        )}
      >
        Text copied
      </div>
    </>
  );
};

Table.propTypes = {
  classNames: PropTypes.shape({
    tableContainerClassName: PropTypes.string,
    tableClassName: PropTypes.string,
    tableRowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    tableHeadClassName: PropTypes.string,
    tableCellClassName: PropTypes.string,
  }),
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.isRequired,
    }),
  ),
  columns: columnsPropType,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  defaultColumns: columnsPropType,
  primaryAction: PropTypes.shape({
    name: PropTypes.string.isRequired,
    actionFn: PropTypes.func.isRequired,
    component: PropTypes.node,
    args: PropTypes.object,
  }),
  secondaryActions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      actionFn: PropTypes.func.isRequired,
      backgroundColor: PropTypes.string,
    }),
  ),
  setHasActiveEdits: PropTypes.func,
  setQuantity: PropTypes.func,
  isDataLoading: PropTypes.bool,
  tableAction: PropTypes.shape({
    name: PropTypes.string.isRequired,
    actionFn: PropTypes.func.isRequired,
  }),
  isActionsBarAbsent: PropTypes.bool,
  requiredButtonList: PropTypes.arrayOf(PropTypes.string),
  getSelectedRows: PropTypes.func.isRequired,
};

export default Table;
